// FloaterCard.jsx
import { useEffect, useRef, useState, useCallback } from "react";
import styled, { keyframes, css } from "styled-components";
import { FloatContent } from "./floatContent";

const AUTO_INTERVAL_MS = 3500;

const FloaterCard = ({ items = FloatContent, intervalMs = AUTO_INTERVAL_MS }) => {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [hidden, setHidden] = useState(false); // NEW
  const timer = useRef(null);

  const next = useCallback(() => {
    setIdx((i) => (i + 1) % items.length);
  }, [items.length]);

  useEffect(() => {
    if (items.length <= 1 || paused || hidden) return; // pause when hidden too
    timer.current = setInterval(next, intervalMs);
    return () => clearInterval(timer.current);
  }, [next, intervalMs, paused, hidden, items.length]);

  if (!items || items.length === 0) return null;
  const current = items[idx];

  return (
    <Container
      id="floater-card-root"
      role="complementary"
      aria-label="promotional carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
      onTouchEnd={() => setPaused(false)}
      $hidden={hidden}
    >
      <Card
        onClick={() => current?.url && !hidden && (window.location.href = current.url)}
        aria-hidden={hidden}
        tabIndex={hidden ? -1 : 0}
      >
        <Image $img={current.img} key={current.id} />
        {current.text ? <Banner>{current.text}</Banner> : null}
      </Card>

      {/* dots */}
      {!hidden && (
        <Dots>
          {items.map((_, i) => (
            <Dot
              key={i}
              aria-label={`go to slide ${i + 1}`}
              $active={i === idx}
              onClick={(e) => {
                e.stopPropagation();
                setIdx(i);
              }}
            />
          ))}
        </Dots>
      )}

      {/* hide / show tab — subtle chevron */}
      <ToggleHide
        aria-label={hidden ? "show card" : "hide card"}
        onClick={(e) => {
          e.stopPropagation();
          setHidden((h) => !h);
        }}
        $hidden={hidden}
        title={hidden ? "Show" : "Hide"}
      >
        <Chevron $hidden={hidden} viewBox="0 0 24 24">
          {/* left-pointing chevron; rotates when hidden */}
          <path d="M15.5 5l-7 7 7 7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </Chevron>
      </ToggleHide>
    </Container>
  );
};

export default FloaterCard;

/* ------------ styles ------------- */

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px) scale(0.995); }
  to   { opacity: 1; transform: translateY(0)    scale(1); }
`;

const Container = styled.aside`
  position: fixed;
  right: 32px;
  bottom: 96px;
  z-index: 999;
  width: 220px;
  height: 280px;
  pointer-events: auto;
  transition: transform .25s ease, right .25s ease;

  /* when hidden, slide it so ~80% of the card goes offscreen, leaving a small tab */
  ${({ $hidden }) =>
    $hidden &&
    css`
      transform: translateX(64%);
      /* on very small screens, slide a bit more */
      @media (max-width: 640px) {
        transform: translateX(72%);
      }
    `}

  @media (max-width: 640px) {
    right: 16px;
    bottom: 24px;
    width: 180px;
    height: 230px;
  }
`;

const Card = styled.button`
  width: 100%;
  height: 100%;
  border: 4px solid #fff;
  border-radius: 20px;
  background: linear-gradient(30deg, #003f6c, #00428c);
  box-shadow:
    rgba(255,255,255,0.08) 0 1px 1px inset,
    rgba(50,50,93,0.25) 0 40px 80px -20px,
    rgba(0,0,0,0.3) 0 24px 48px -24px;
  overflow: hidden;
  padding: 0;
  display: block;
  cursor: pointer;
  transition: transform .2s ease, box-shadow .2s ease;

  &:hover { transform: translateY(-2px); }
  &:focus { outline: none; box-shadow: 0 0 0 3px rgba(255,255,255,.5); }
`;

const Image = styled.div`
  width: 100%;
  height: 100%;
  background: ${({ $img }) => ($img ? css`url(${$img}) center/cover no-repeat` : "#0b3d60")};
  animation: ${fadeIn} .45s ease both;
`;

const Banner = styled.div`
  position: absolute;
  left: 12px;
  bottom: 12px;
  right: 12px;
  background: rgba(0, 0, 0, 0.35);
  color: #fff;
  padding: 8px 10px;
  font-weight: 700;
  font-size: 13px;
  line-height: 1.25;
  border-radius: 12px;
  backdrop-filter: blur(4px);
`;

const Dots = styled.div`
  position: absolute;
  left: 0; right: 0;
  bottom: 18px;
  display: flex;
  justify-content: center;
  gap: 8px;
  pointer-events: none;
`;

const Dot = styled.span`
  width: 8px; height: 8px; border-radius: 999px;
  background: ${({ $active }) => ($active ? "#1dd3b0" : "rgba(255,255,255,0.5)")};
  box-shadow: 0 0 0 2px rgba(0,0,0,0.15);
  pointer-events: auto;
  cursor: pointer;
  transition: transform .15s ease, background .2s ease;
  &:hover { transform: scale(1.15); }
`;

/* ---- subtle hide/show tab ---- */
const ToggleHide = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 22px;
  height: 48px;
  border-radius: 12px;
  border: 0;
  background: rgba(11, 61, 96, 0.92);
  color: #e7f6ff;
  display: grid;
  place-items: center;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(0,0,0,.25);
  transition: opacity .2s ease, background .2s ease;
  opacity: 0.8;

  &:hover { opacity: 1; }
  ${({ $hidden }) =>
    $hidden &&
    css`
      background: rgba(11, 61, 96, 0.85);
    `}
`;

const Chevron = styled.svg`
//   width: 16px;
  height: 16px;
  transition: transform .2s ease;
  transform: rotate(180deg);
  ${({ $hidden }) => $hidden && css`transform: rotate(0deg);`}
`;
